const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const diffDays = (a, b) => Math.round((startOfDay(a) - startOfDay(b)) / 86400000);

/** Cobranza esperada según avance del plazo (adelanto ~20% al inicio, 100% al vencimiento). */
function pctCobroEsperado(avancePlazo) {
  return Math.round((20 + 80 * Math.min(1, Math.max(0, avancePlazo))) * 10) / 10;
}

export function evaluarCobranzaPlazo(exp) {
  const {
    diasPrometidos,
    diasTranscurridos,
    pctCobrado,
    entregado,
    tonoDemora,
    adeudado,
    estadoPagoAdmin
  } = exp;

  if (pctCobrado >= 100 || estadoPagoAdmin === 'PAGADO') {
    return {
      estadoCobranza: 'ok',
      etiquetaCobranza: 'Cobrado al 100%',
      alertaCobranza: false,
      pctEsperadoCobro: 100
    };
  }

  const avancePlazo = diasPrometidos > 0
    ? Math.min(1, diasTranscurridos / diasPrometidos)
    : 1;
  const diasRestantes = Math.max(0, diasPrometidos - diasTranscurridos);
  const pctEsperado = pctCobroEsperado(avancePlazo);
  const base = { pctEsperadoCobro: pctEsperado };

  if (entregado && adeudado > 0) {
    return {
      ...base,
      estadoCobranza: 'danger',
      etiquetaCobranza: `Entregado con saldo pendiente (${pctCobrado}% cobrado)`,
      alertaCobranza: true
    };
  }

  if (tonoDemora === 'danger' && !entregado) {
    if (pctCobrado < 80) {
      return {
        ...base,
        estadoCobranza: 'danger',
        etiquetaCobranza: `Vencido — solo ${pctCobrado}% cobrado`,
        alertaCobranza: true
      };
    }
    return {
      ...base,
      estadoCobranza: 'warning',
      etiquetaCobranza: `Vencido — ${pctCobrado}% cobrado, saldo por cerrar`,
      alertaCobranza: pctCobrado < 100
    };
  }

  if (tonoDemora === 'warning') {
    if (pctCobrado < 60) {
      return {
        ...base,
        estadoCobranza: 'danger',
        etiquetaCobranza: `Vence pronto — ${pctCobrado}% cobrado (se esperaba ~${pctEsperado}%)`,
        alertaCobranza: true
      };
    }
    return {
      ...base,
      estadoCobranza: 'ok',
      etiquetaCobranza: `Vence pronto — ${pctCobrado}% cobrado, en ritmo`,
      alertaCobranza: false
    };
  }

  // En plazo: adelanto típico al inicio del proyecto
  if (avancePlazo < 0.35 && pctCobrado >= 15) {
    return {
      ...base,
      estadoCobranza: 'ok',
      etiquetaCobranza: `En plazo (${diasRestantes} d rest.) — adelanto recibido`,
      alertaCobranza: false
    };
  }

  if (pctCobrado >= pctEsperado - 15) {
    return {
      ...base,
      estadoCobranza: 'ok',
      etiquetaCobranza: `En plazo — ${pctCobrado}% cobrado, en ritmo`,
      alertaCobranza: false
    };
  }

  return {
    ...base,
    estadoCobranza: 'warning',
    etiquetaCobranza: `Cobranza atrasada (${pctCobrado}% vs ~${pctEsperado}% esperado al día de hoy)`,
    alertaCobranza: true
  };
}

export function enriquecerExpedienteAnalitico(exp) {
  const hoy = startOfDay(new Date());
  const creacion = startOfDay(exp.fechaCreacion);
  const limite = startOfDay(exp.fechaLimite);
  const entrega = exp.fechaEntregaReal ? startOfDay(exp.fechaEntregaReal) : null;

  const diasPrometidos = diffDays(limite, creacion);
  const diasTranscurridos = entrega ? diffDays(entrega, creacion) : diffDays(hoy, creacion);

  let diasRetraso = 0;
  let etiquetaDemora = 'En plazo';
  let tonoDemora = 'ok';

  if (exp.entregado && entrega) {
    diasRetraso = diffDays(entrega, limite);
    if (diasRetraso > 0) {
      etiquetaDemora = `Entregado +${diasRetraso} d`;
      tonoDemora = 'danger';
    } else if (diasRetraso < 0) {
      etiquetaDemora = `Entregado ${Math.abs(diasRetraso)} d antes`;
      tonoDemora = 'ok';
    } else {
      etiquetaDemora = 'Entregado a tiempo';
      tonoDemora = 'ok';
    }
  } else if (limite < hoy) {
    diasRetraso = diffDays(hoy, limite);
    etiquetaDemora = `Vencido hace ${diasRetraso} d`;
    tonoDemora = 'danger';
  } else {
    const faltan = diffDays(limite, hoy);
    etiquetaDemora = faltan <= 7 ? `Vence en ${faltan} d` : 'En plazo';
    tonoDemora = faltan <= 7 ? 'warning' : 'ok';
  }

  const pctCobrado = exp.montoAPagar > 0
    ? Math.round((exp.totalAbonado / exp.montoAPagar) * 1000) / 10
    : 0;

  const base = {
    ...exp,
    diasPrometidos,
    diasTranscurridos,
    diasRetraso,
    etiquetaDemora,
    tonoDemora,
    pctCobrado
  };

  return { ...base, ...evaluarCobranzaPlazo(base) };
}

function calcularScoresRentabilidad(items) {
  const maxM2 = Math.max(...items.map((e) => e.precioPorM2 || 0), 1);
  const maxMonto = Math.max(...items.map((e) => e.montoAPagar || 0), 1);

  return items.map((exp) => {
    const parteM2 = exp.precioPorM2 ? (exp.precioPorM2 / maxM2) * 40 : 0;
    const parteCobro = (exp.pctCobrado / 100) * 35;
    const parteMonto = (exp.montoAPagar / maxMonto) * 25;
    const scoreRentabilidad = Math.round((parteM2 + parteCobro + parteMonto) * 10) / 10;

    let interpretacion = '';
    if (exp.precioPorM2 && exp.pctCobrado >= 90) {
      interpretacion = 'Alto precio/m² y bien cobrado';
    } else if (exp.precioPorM2 && exp.pctCobrado < 50) {
      interpretacion = 'Buen ticket, cobranza débil';
    } else if (exp.montoAPagar >= maxMonto * 0.7) {
      interpretacion = 'Proyecto de alto monto';
    } else if (exp.pctCobrado >= 100) {
      interpretacion = 'Cobrado al 100%';
    } else {
      interpretacion = 'Rentabilidad media';
    }

    return {
      ...exp,
      scoreRentabilidad,
      interpretacionRentabilidad: interpretacion,
      desgloseRentabilidad: {
        precioM2: Math.round(parteM2 * 10) / 10,
        cobranza: Math.round(parteCobro * 10) / 10,
        monto: Math.round(parteMonto * 10) / 10
      }
    };
  });
}

export function buildAnalisisMaestra(datos = []) {
  const base = datos.map(enriquecerExpedienteAnalitico);
  const conScore = calcularScoresRentabilidad(base);

  const rentabilidadExpedientes = [...conScore]
    .sort((a, b) => b.scoreRentabilidad - a.scoreRentabilidad);

  const demorasExpedientes = [...conScore]
    .sort((a, b) => {
      const prio = (e) => {
        if (!e.entregado && e.tonoDemora === 'danger') return 1000 + e.diasRetraso;
        if (e.entregado && e.diasRetraso > 0) return 500 + e.diasRetraso;
        return e.diasRetraso;
      };
      return prio(b) - prio(a);
    });

  const porTipoMap = {};
  for (const exp of conScore) {
    const key = exp.tipo.nombre;
    if (!porTipoMap[key]) {
      porTipoMap[key] = {
        tipo: key,
        cantidad: 0,
        vendido: 0,
        cobrado: 0,
        conM2: 0,
        sumM2: 0,
        vencidos: 0,
        retrasos: [],
        scores: []
      };
    }
    const g = porTipoMap[key];
    g.cantidad += 1;
    g.vendido += exp.montoAPagar || 0;
    g.cobrado += exp.totalAbonado || 0;
    if (exp.precioPorM2) {
      g.conM2 += 1;
      g.sumM2 += exp.precioPorM2;
    }
    if (exp.tonoDemora === 'danger') g.vencidos += 1;
    if (exp.diasRetraso > 0) g.retrasos.push(exp.diasRetraso);
    g.scores.push(exp.scoreRentabilidad);
  }

  const rentabilidadPorServicio = Object.values(porTipoMap)
    .map((g) => ({
      ...g,
      precioM2Prom: g.conM2 ? Math.round((g.sumM2 / g.conM2) * 100) / 100 : null,
      pctCobrado: g.vendido > 0 ? Math.round((g.cobrado / g.vendido) * 1000) / 10 : 0,
      scoreProm: g.scores.length
        ? Math.round((g.scores.reduce((s, v) => s + v, 0) / g.scores.length) * 10) / 10
        : 0,
      pctVencidos: g.cantidad > 0 ? Math.round((g.vencidos / g.cantidad) * 1000) / 10 : 0,
      retrasoProm: g.retrasos.length
        ? Math.round(g.retrasos.reduce((s, v) => s + v, 0) / g.retrasos.length)
        : 0
    }))
    .sort((a, b) => b.scoreProm - a.scoreProm);

  const clientesMap = {};
  for (const exp of conScore) {
    const id = exp.cliente.id;
    if (!clientesMap[id]) {
      clientesMap[id] = {
        cliente: exp.cliente,
        expedientes: [],
        vendido: 0,
        cobrado: 0,
        saldo: 0,
        retrasos: [],
        scores: []
      };
    }
    const c = clientesMap[id];
    c.expedientes.push(exp);
    c.vendido += exp.montoAPagar || 0;
    c.cobrado += exp.totalAbonado || 0;
    c.saldo += exp.adeudado || 0;
    if (exp.diasRetraso > 0) c.retrasos.push(exp.diasRetraso);
    c.scores.push(exp.scoreRentabilidad);
  }

  const clientesRanking = Object.values(clientesMap)
    .map((c) => {
      const cantidad = c.expedientes.length;
      const pctCobrado = c.vendido > 0 ? Math.round((c.cobrado / c.vendido) * 1000) / 10 : 0;
      const retrasoProm = c.retrasos.length
        ? Math.round(c.retrasos.reduce((s, v) => s + v, 0) / c.retrasos.length)
        : 0;
      const scoreRentabilidadProm = c.scores.length
        ? Math.round((c.scores.reduce((s, v) => s + v, 0) / c.scores.length) * 10) / 10
        : 0;

      let scoreValor = cantidad * 15;
      scoreValor += pctCobrado * 0.4;
      scoreValor += Math.min(c.cobrado / 5000, 20);
      scoreValor -= retrasoProm * 1.5;
      scoreValor -= (c.saldo / Math.max(c.vendido, 1)) * 25;
      scoreValor = Math.round(scoreValor * 10) / 10;

      let interpretacion = '';
      if (cantidad >= 3 && pctCobrado >= 80) {
        interpretacion = 'Cliente recurrente y confiable';
      } else if (cantidad >= 2) {
        interpretacion = 'Cliente recurrente';
      } else if (c.saldo > c.vendido * 0.5) {
        interpretacion = 'Saldo pendiente alto';
      } else if (pctCobrado >= 95) {
        interpretacion = 'Paga bien, primer proyecto';
      } else if (retrasoProm > 10) {
        interpretacion = 'Historial con demoras';
      } else {
        interpretacion = 'Evaluar caso a caso';
      }

      return {
        ...c,
        cantidad,
        pctCobrado,
        retrasoProm,
        scoreRentabilidadProm,
        scoreValor,
        interpretacionCliente: interpretacion
      };
    })
    .sort((a, b) => b.scoreValor - a.scoreValor);

  const topRentable = rentabilidadExpedientes[0] || null;
  const peorDemora = demorasExpedientes.find((e) => e.tonoDemora === 'danger' || e.diasRetraso > 0) || demorasExpedientes[0] || null;
  const mejorCliente = clientesRanking[0] || null;

  const vencidos = conScore.filter((e) => !e.entregado && e.tonoDemora === 'danger').length;
  const cobradoTotal = conScore.reduce((s, e) => s + (e.totalAbonado || 0), 0);
  const vendidoTotal = conScore.reduce((s, e) => s + (e.montoAPagar || 0), 0);

  return {
    total: conScore.length,
    resumen: {
      topRentable,
      peorDemora,
      mejorCliente,
      vencidos,
      pctCobradoGlobal: vendidoTotal > 0 ? Math.round((cobradoTotal / vendidoTotal) * 1000) / 10 : 0
    },
    rentabilidadExpedientes,
    demorasExpedientes,
    rentabilidadPorServicio,
    clientesRanking,
    datosEnriquecidos: conScore
  };
}
